import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { auth } from '../../firebaseConfig';
import { database } from '../../firebaseConfig';
import { ref, onValue, push, update, get } from 'firebase/database';
import LeadSourceSelector from '../Components/LeadSourceSelector';
import ProductManager from '../Components/ProductManager';
import SocialProofManager from '../Components/SocialProofManager';
import './BusinessAnalysisAdd.css';

function BusinessAnalysisAdd() {
    const navigate = useNavigate();
    const { analysisId } = useParams();
    const isEditing = !!analysisId;
    
    const [analysisData, setAnalysisData] = useState({
        // Basic Information
        name: '',
        brandType: 'personal', // personal | corporate
        niche: '',
        targetAudience: {
            description: '',
            ageRange: '25-34', // 18-24, 25-34, 35-44, 45-54, 55-64, 65+
            gender: 'all', // male, female, all
            incomeClass: [], // Array: 'low', 'middle', 'high' - multiple selection
            education: 'bachelor', // high-school, vocational, bachelor, master, phd
            interests: '',
            painPoints: '',
            goals: '',
            antiCustomer: '' // Who is NOT your target customer
        },
        marketSaturation: '100-1000', // under100 | 100-1000 | over1000
        geoFocus: '',
        language: 'german',
        foundingDate: '',
        
        // Branding & Brand Image
        branding: {
            embodiment: '',
            emotionalTriggers: '',
            design: '', // Design & Farbpsychologie
            wording: '', // Tonalität & Sprache
            values: '', // Kernwerte
            hiddenPsychology: '', // Versteckte Psychologie
            brandingQuality: 6, // 1-10 slider
            messageDesignAlignment: 6, // 1-10 slider
            photoQuality: 6, // 1-10 slider
            videoMaterial: 5, // 1-10 slider
        },
        
        // Lead Sources (starts empty)
        leadSources: [],
        
        // Products (starts empty)
        products: [],
        
        // Social Proof (starts empty)
        socialProof: [],
        
        // Meta
        status: 'draft',
        createdAt: '',
        updatedAt: ''
    });
    
    const [currentSection, setCurrentSection] = useState('basic');
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    const [currentAnalysisId, setCurrentAnalysisId] = useState(analysisId);

    // Auto-save function with improved error handling
    const autoSave = useCallback(async (data) => {
        if (!auth.currentUser) {
            console.warn('Auto-save skipped: No authenticated user');
            return;
        }
        
        // Don't save if no meaningful data exists
        if (!data.name || data.name.trim().length === 0) {
            console.log('Auto-save skipped: No analysis name provided');
            return;
        }
        
        setIsSaving(true);
        try {
            const timestamp = new Date().toISOString();
            const userId = auth.currentUser.uid;
            
            // Prepare clean data for saving
            const saveData = {
                // Basic Info
                name: data.name.trim(),
                brandType: data.brandType || 'personal',
                niche: data.niche || '',
                targetAudience: data.targetAudience || {
                    description: '',
                    ageRange: '25-34',
                    gender: 'all',
                    incomeClass: [],
                    education: 'bachelor',
                    interests: '',
                    painPoints: '',
                    goals: '',
                    antiCustomer: ''
                },
                marketSaturation: data.marketSaturation || 'under100',
                geoFocus: data.geoFocus || '',
                language: data.language || '',
                foundingDate: data.foundingDate || '',
                
                // Branding
                branding: {
                    embodiment: data.branding?.embodiment || '',
                    emotionalTriggers: data.branding?.emotionalTriggers || '',
                    design: data.branding?.design || '',
                    wording: data.branding?.wording || '',
                    values: data.branding?.values || '',
                    hiddenPsychology: data.branding?.hiddenPsychology || '',
                    brandingQuality: data.branding?.brandingQuality || 5,
                    messageDesignAlignment: data.branding?.messageDesignAlignment || 5,
                    photoQuality: data.branding?.photoQuality || 5,
                    videoMaterial: data.branding?.videoMaterial || 5,
                },
                
                // Lead Sources
                leadSources: data.leadSources || [],
                
                // Products (nur Produkte mit Namen speichern)
                products: (data.products || []).filter(p => p.name && p.name.trim().length > 0),
                
                // Social Proof
                socialProof: data.socialProof || [],
                
                // Meta information
                status: data.status || 'draft',
                updatedAt: timestamp
            };
            
            if (currentAnalysisId) {
                // Update existing analysis
                console.log(`Updating analysis ${currentAnalysisId} for user ${userId}`);
                const analysisRef = ref(database, `businessAnalyses/${userId}/${currentAnalysisId}`);
                await update(analysisRef, saveData);
                console.log('Analysis updated successfully');
            } else {
                // Create new analysis
                console.log(`Creating new analysis for user ${userId}`);
                const analysesRef = ref(database, `businessAnalyses/${userId}`);
                saveData.createdAt = timestamp;
                
                const newRef = await push(analysesRef, saveData);
                const newAnalysisId = newRef.key;
                setCurrentAnalysisId(newAnalysisId);
                
                console.log(`New analysis created with ID: ${newAnalysisId}`);
                
                // Update URL to edit mode without page reload
                window.history.replaceState(
                    null, 
                    '', 
                    `/business-analysis/edit/${newAnalysisId}`
                );
            }
            
            setLastSaved(new Date());
            console.log('Auto-save completed successfully');
            
        } catch (error) {
            console.error('Auto-save error:', error);
            console.error('Error details:', {
                code: error.code,
                message: error.message,
                userId: auth.currentUser?.uid,
                analysisId: currentAnalysisId
            });
            
            // Show user-friendly error (optional)
            // You could add a toast notification here
        }
        setIsSaving(false);
    }, [currentAnalysisId]);

    // Handle section change with immediate save
    const handleSectionChange = useCallback(async (newSection) => {
        // Save immediately when changing sections if name exists
        if (analysisData.name.trim() && auth.currentUser) {
            await autoSave(analysisData);
        }
        
        // Scroll to top when changing sections
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        setCurrentSection(newSection);
    }, [analysisData, autoSave]);

    // Load existing analysis if editing
    useEffect(() => {
        if (isEditing && analysisId && auth.currentUser) {
            console.log(`Loading analysis ${analysisId} for user ${auth.currentUser.uid}`);
            
            const analysisRef = ref(database, `businessAnalyses/${auth.currentUser.uid}/${analysisId}`);
            get(analysisRef).then((snapshot) => {
                if (snapshot.exists()) {
                    const loadedData = snapshot.val();
                    console.log('Analysis loaded successfully:', loadedData);
                    
                    // Convert old incomeClass string to array if needed
                    let incomeClass = loadedData.targetAudience?.incomeClass || [];
                    if (typeof incomeClass === 'string') {
                        incomeClass = [incomeClass];
                    }
                    
                    // Ensure all required fields exist with defaults
                    const completeData = {
                        // Basic Info
                        name: loadedData.name || '',
                        brandType: loadedData.brandType || 'personal',
                        niche: loadedData.niche || '',
                        targetAudience: {
                            description: loadedData.targetAudience?.description || '',
                            ageRange: loadedData.targetAudience?.ageRange || '25-34',
                            gender: loadedData.targetAudience?.gender || 'all',
                            incomeClass: incomeClass,
                            education: loadedData.targetAudience?.education || 'bachelor',
                            interests: loadedData.targetAudience?.interests || '',
                            painPoints: loadedData.targetAudience?.painPoints || '',
                            goals: loadedData.targetAudience?.goals || '',
                            antiCustomer: loadedData.targetAudience?.antiCustomer || ''
                        },
                        marketSaturation: loadedData.marketSaturation || 'under100',
                        geoFocus: loadedData.geoFocus || '',
                        language: loadedData.language || '',
                        foundingDate: loadedData.foundingDate || '',
                        
                        // Branding with defaults
                        branding: {
                            embodiment: loadedData.branding?.embodiment || '',
                            emotionalTriggers: loadedData.branding?.emotionalTriggers || '',
                            design: loadedData.branding?.design || '',
                            wording: loadedData.branding?.wording || '',
                            values: loadedData.branding?.values || '',
                            hiddenPsychology: loadedData.branding?.hiddenPsychology || '',
                            brandingQuality: loadedData.branding?.brandingQuality || 5,
                            messageDesignAlignment: loadedData.branding?.messageDesignAlignment || 5,
                            photoQuality: loadedData.branding?.photoQuality || 5,
                            videoMaterial: loadedData.branding?.videoMaterial || 5,
                        },
                        
                        // Lead Sources with defaults
                        leadSources: loadedData.leadSources || [],
                        
                        // Products (nur Produkte mit Namen laden)
                        products: (loadedData.products || []).filter(p => p.name && p.name.trim().length > 0),
                        
                        // Social Proof
                        socialProof: loadedData.socialProof || [],
                        
                        // Meta
                        status: loadedData.status || 'draft',
                        createdAt: loadedData.createdAt || new Date().toISOString(),
                        updatedAt: loadedData.updatedAt || new Date().toISOString()
                    };
                    
                    setAnalysisData(completeData);
                } else {
                    console.warn(`Analysis ${analysisId} not found for user ${auth.currentUser.uid}`);
                    // Redirect to new analysis if not found
                    navigate('/business-analysis/add');
                }
            }).catch((error) => {
                console.error('Error loading analysis:', error);
                // Redirect to dashboard on error
                navigate('/business-analysis');
            });
        }
    }, [isEditing, analysisId, navigate]);

    // Auto-save on data changes (debounced)
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (analysisData.name.trim()) { // Only save if name is provided
                autoSave(analysisData);
            }
        }, 2000); // 2 second delay

        return () => clearTimeout(timeoutId);
    }, [analysisData, autoSave]);

    const updateField = (field, value) => {
        setAnalysisData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const updateNestedField = (section, field, value) => {
        setAnalysisData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const toggleArrayValue = (section, field, value) => {
        setAnalysisData(prev => {
            const currentArray = prev[section][field] || [];
            const newArray = currentArray.includes(value)
                ? currentArray.filter(item => item !== value)
                : [...currentArray, value];
            
            return {
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: newArray
                }
            };
        });
    };

    const addArrayItem = (arrayName, newItem) => {
        setAnalysisData(prev => ({
            ...prev,
            [arrayName]: [...prev[arrayName], { ...newItem, id: Date.now() }]
        }));
    };

    const updateArrayItem = (arrayName, itemId, field, value) => {
        setAnalysisData(prev => ({
            ...prev,
            [arrayName]: prev[arrayName].map(item => 
                item.id === itemId ? { ...item, [field]: value } : item
            )
        }));
    };

    const removeArrayItem = (arrayName, itemId) => {
        setAnalysisData(prev => ({
            ...prev,
            [arrayName]: prev[arrayName].filter(item => item.id !== itemId)
        }));
    };

    const sections = [
        { id: 'basic', label: 'Grunddaten', icon: 'fas fa-info-circle', description: 'Name, Branche & Zielgruppe' },
        { id: 'branding', label: 'Markenimage', icon: 'fas fa-palette', description: 'Design & Emotionale Wirkung' },
        { id: 'leadSources', label: 'Lead-Quellen', icon: 'fas fa-funnel-dollar', description: 'Kundenakquise-Kanäle' },
        { id: 'products', label: 'Produkte', icon: 'fas fa-box', description: 'Angebote & Preisstruktur' },
        { id: 'socialProof', label: 'Social Proof', icon: 'fas fa-certificate', description: 'Vertrauen & Bewertungen' },
        { id: 'analysis', label: 'Analyse', icon: 'fas fa-chart-line', description: 'Business Stärke Bewertung' }
    ];

    // Get score color based on value
    const getScoreColor = (score) => {
        if (score >= 80) return '#00ff88';  // Neon Green - Excellent
        if (score >= 65) return '#e91e63';  // Hot Pink - Good
        if (score >= 50) return '#ffd93d';  // Cyber Yellow - Average
        return '#ff6b9d';  // Light Pink - Needs Work
    };

    // Business Strength Analysis Function
    const calculateBusinessStrength = () => {
        let scores = {
            branding: 0,
            leadDiversity: 0,
            productPortfolio: 0,
            socialProof: 0
        };
        let recommendations = [];

        // 1. Branding Score (0-100)
        const brandingAvg = (
            analysisData.branding.brandingQuality +
            analysisData.branding.messageDesignAlignment +
            analysisData.branding.photoQuality +
            analysisData.branding.videoMaterial
        ) / 4;
        scores.branding = (brandingAvg / 10) * 100;
        
        if (scores.branding < 60) {
            recommendations.push({
                category: 'Branding',
                priority: 'high',
                message: 'Ihr Branding benötigt mehr Fokus. Investieren Sie in professionelle Fotografie und konsistentes Design.'
            });
        } else if (scores.branding < 80) {
            recommendations.push({
                category: 'Branding',
                priority: 'medium',
                message: 'Gute Basis! Erwägen Sie professionelles Videomaterial, um sich abzuheben.'
            });
        }

        // 2. Lead Source Diversity (0-100)
        const leadSourceCount = analysisData.leadSources.length;
        const leadSourceAvgStrength = analysisData.leadSources.reduce((sum, source) => sum + source.strength, 0) / leadSourceCount;
        scores.leadDiversity = Math.min(
            (leadSourceCount * 15) + (leadSourceAvgStrength * 5), 
            100
        );

        if (leadSourceCount < 2) {
            recommendations.push({
                category: 'Lead-Quellen',
                priority: 'high',
                message: 'Kritisch: Sie setzen auf zu wenige Lead-Quellen. Diversifizieren Sie zur Risikominimierung!'
            });
        } else if (leadSourceCount < 4) {
            recommendations.push({
                category: 'Lead-Quellen',
                priority: 'medium',
                message: 'Fügen Sie weitere Akquise-Kanäle hinzu für mehr Stabilität und Wachstum.'
            });
        }

        // 3. Product Portfolio Score (0-100)
        const productCount = analysisData.products.filter(p => p.name && p.name.trim().length > 0).length;
        const hasHighTicket = analysisData.products.some(p => ['500-1000', '1000-5000', '5000-10000', '10000-20000', '20000-50000', 'over50000'].includes(p.cost));
        const hasUltraHighTicket = analysisData.products.some(p => ['10000-20000', '20000-50000', 'over50000'].includes(p.cost));
        const hasRecurring = analysisData.products.some(p => p.paymentType === 'monthly');
        const hasRevolutionary = analysisData.products.some(p => p.exclusivity === 'revolutionary');
        
        scores.productPortfolio = Math.min(
            (productCount * 15) + 
            (hasHighTicket ? 25 : 0) + 
            (hasUltraHighTicket ? 15 : 0) +
            (hasRecurring ? 30 : 0) +
            (hasRevolutionary ? 15 : 0),
            100
        );

        if (!hasRecurring) {
            recommendations.push({
                category: 'Produkte',
                priority: 'high',
                message: 'Fügen Sie ein Abo-Produkt hinzu für vorhersehbare, wiederkehrende Einnahmen.'
            });
        }
        if (!hasHighTicket) {
            recommendations.push({
                category: 'Produkte',
                priority: 'medium',
                message: 'Erwägen Sie ein Premium High-Ticket Angebot (€500+) für höhere Umsätze pro Kunde.'
            });
        }
        if (hasRevolutionary) {
            recommendations.push({
                category: 'Produkte',
                priority: 'low',
                message: 'Exzellent! Revolutionäre Produkte verschaffen Ihnen einen starken Wettbewerbsvorteil.'
            });
        }

        // 4. Social Proof Score (0-100)
        const proofCount = analysisData.socialProof.length;
        const proofAvgStrength = analysisData.socialProof.reduce((sum, proof) => sum + proof.strength, 0) / proofCount;
        scores.socialProof = Math.min(
            (proofCount * 12) + (proofAvgStrength * 6),
            100
        );

        if (scores.socialProof < 60) {
            recommendations.push({
                category: 'Social Proof',
                priority: 'high',
                message: 'Bauen Sie mehr Vertrauenssignale auf! Sammeln Sie Testimonials und Bewertungen.'
            });
        }

        // Overall Score
        const overallScore = Math.round(
            (scores.branding * 0.30) +
            (scores.leadDiversity * 0.30) +
            (scores.productPortfolio * 0.20) +
            (scores.socialProof * 0.20)
        );

        return {
            scores: {
                ...scores,
                overall: overallScore
            },
            recommendations: recommendations.sort((a, b) => {
                const priorityOrder = { high: 0, medium: 1, low: 2 };
                return priorityOrder[a.priority] - priorityOrder[b.priority];
            }),
            strengths: [
                scores.branding >= 75 && 'Starke Markenidentität',
                scores.leadDiversity >= 75 && 'Diverse Lead-Kanäle',
                scores.productPortfolio >= 75 && 'Solides Produkt-Portfolio',
                scores.socialProof >= 75 && 'Exzellenter Social Proof'
            ].filter(Boolean),
            rating: overallScore >= 80 ? 'Exzellent' : 
                    overallScore >= 65 ? 'Gut' : 
                    overallScore >= 50 ? 'Durchschnittlich' : 
                    overallScore >= 35 ? 'Ausbaufähig' : 'Kritisch'
        };
    };

    return (
        <div className="business-analyse-add">
            {/* Header */}
            <div className="business-analyse-add-header">
                <div className="business-analyse-add-header-content">
                    <div className="business-analyse-add-title-section">
                        <button 
                            className="business-analyse-back-btn"
                            onClick={() => navigate('/business-analysis')}
                        >
                            <i className="fas fa-arrow-left"></i>
                        </button>
                        <div>
                            <h1 className="business-analyse-add-title">
                                {analysisData.name.trim() 
                                    ? analysisData.name 
                                    : (isEditing ? 'Business-Analyse bearbeiten' : 'Neue Business-Analyse')
                                }
                            </h1>
                            <div className="business-analyse-save-status">
                                {isSaving ? (
                                    <span className="saving">
                                        <i className="fas fa-spinner fa-spin"></i>
                                        Speichere...
                                    </span>
                                ) : lastSaved ? (
                                    <span className="saved">
                                        <i className="fas fa-check"></i>
                                        Gespeichert um {lastSaved.toLocaleTimeString()}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="business-analyse-add-container">
                {/* Section Navigation */}
                <div className="business-analyse-add-nav">
                    <div className="business-analyse-progress-header">
                        <h3>Analyse-Bereiche</h3>
                        <div className="business-analyse-progress-bar">
                            <div 
                                className="business-analyse-progress-fill"
                                style={{ width: `${((sections.findIndex(s => s.id === currentSection) + 1) / sections.length) * 100}%` }}
                            ></div>
                        </div>
                    </div>
                    {sections.map((section, index) => {
                        const isCompleted = sections.findIndex(s => s.id === currentSection) > index;
                        const isCurrent = currentSection === section.id;
                        
                        return (
                            <button
                                key={section.id}
                                className={`business-analyse-nav-item ${isCurrent ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                                onClick={() => handleSectionChange(section.id)}
                            >
                                <div className="business-analyse-nav-icon">
                                    <i className={isCompleted ? 'fas fa-check' : section.icon}></i>
                                </div>
                                <div className="business-analyse-nav-content">
                                    <span className="business-analyse-nav-label">{section.label}</span>
                                    <span className="business-analyse-nav-description">{section.description}</span>
                                </div>
                                <div className="business-analyse-nav-number">{index + 1}</div>
                            </button>
                        );
                    })}
                </div>

                {/* Content Area */}
                <div className="business-analyse-add-content">
                    {currentSection === 'basic' && (
                        <div className="business-analyse-section">
                            <div className="business-analyse-section-header">
                                <h2 className="business-analyse-section-title">
                                    <i className="fas fa-info-circle"></i>
                                    Grunddaten
                                </h2>
                                <p className="business-analyse-section-subtitle">
                                    Legen Sie die Basis für Ihre Business-Analyse fest
                                </p>
                            </div>
                            
                            <div className="business-analyse-form-group">
                                <label>
                                    <i className="fas fa-tag"></i>
                                    Analyse-Name *
                                </label>
                                <input
                                    type="text"
                                    value={analysisData.name}
                                    onChange={(e) => updateField('name', e.target.value)}
                                    placeholder="z.B. Meine Business-Analyse 2024"
                                    className={`business-analyse-input ${analysisData.name ? 'filled' : ''}`}
                                />
                                <div className="business-analyse-input-helper">
                                    Geben Sie Ihrer Analyse einen aussagekräftigen Namen
                                </div>
                            </div>

                                <div className="business-analyse-form-group">
                                    <label>
                                        <i className="fas fa-building"></i>
                                        Marken-Typ
                                    </label>
                                    <div className="business-analyse-radio-group">
                                        <label className="business-analyse-radio-option">
                                            <input
                                                type="radio"
                                                name="brandType"
                                                value="personal"
                                                checked={analysisData.brandType === 'personal'}
                                                onChange={(e) => updateField('brandType', e.target.value)}
                                            />
                                            <div className="business-analyse-radio-content">
                                                <i className="fas fa-user"></i>
                                                <span>Personal Brand</span>
                                                <small>Persönliche Marke</small>
                                            </div>
                                        </label>
                                        <label className="business-analyse-radio-option">
                                            <input
                                                type="radio"
                                                name="brandType"
                                                value="corporate"
                                                checked={analysisData.brandType === 'corporate'}
                                                onChange={(e) => updateField('brandType', e.target.value)}
                                            />
                                            <div className="business-analyse-radio-content">
                                                <i className="fas fa-building"></i>
                                                <span>Corporate Brand</span>
                                                <small>Unternehmensmarke</small>
                                            </div>
                                        </label>
                                </div>
                            </div>

                            <div className="business-analyse-form-group">
                                <label>
                                    <i className="fas fa-bullseye"></i>
                                    Nische
                                </label>
                                <input
                                    type="text"
                                    value={analysisData.niche}
                                    onChange={(e) => updateField('niche', e.target.value)}
                                    placeholder="z.B. Digital Marketing, Gesundheit & Wellness, E-Commerce"
                                    className={`business-analyse-input ${analysisData.niche ? 'filled' : ''}`}
                                />
                                <div className="business-analyse-input-helper">
                                    In welcher Branche oder Nische sind Sie tätig?
                                </div>
                            </div>

                            {/* Target Audience - Expanded Section */}
                            <div className="business-analyse-form-section">
                                <h3 className="business-analyse-form-section-title">
                                    <i className="fas fa-users"></i>
                                    Zielgruppe
                                </h3>
                                <p className="business-analyse-form-section-subtitle">
                                    Definieren Sie Ihre ideale Zielgruppe im Detail
                                </p>

                                <div className="business-analyse-form-group">
                                    <label>
                                        <i className="fas fa-align-left"></i>
                                        Allgemeine Beschreibung
                                </label>
                                <textarea
                                        value={analysisData.targetAudience.description}
                                        onChange={(e) => updateNestedField('targetAudience', 'description', e.target.value)}
                                        placeholder="Geben Sie eine allgemeine Beschreibung Ihrer Zielgruppe ein..."
                                        className={`business-analyse-textarea ${analysisData.targetAudience.description ? 'filled' : ''}`}
                                        rows="3"
                                    />
                            </div>

                                <div className="business-analyse-form-group">
                                    <label>
                                        <i className="fas fa-venus-mars"></i>
                                        Geschlecht
                                    </label>
                                    <div className="business-analyse-radio-group">
                                        <label className="business-analyse-radio-option">
                                            <input
                                                type="radio"
                                                name="targetGender"
                                                value="all"
                                                checked={analysisData.targetAudience.gender === 'all'}
                                                onChange={(e) => updateNestedField('targetAudience', 'gender', e.target.value)}
                                            />
                                            <div className="business-analyse-radio-content">
                                                <i className="fas fa-users"></i>
                                                <span>Alle</span>
                                                <small>Geschlechter</small>
                                            </div>
                                        </label>
                                        <label className="business-analyse-radio-option">
                                            <input
                                                type="radio"
                                                name="targetGender"
                                                value="male"
                                                checked={analysisData.targetAudience.gender === 'male'}
                                                onChange={(e) => updateNestedField('targetAudience', 'gender', e.target.value)}
                                            />
                                            <div className="business-analyse-radio-content">
                                                <i className="fas fa-mars"></i>
                                                <span>Männlich</span>
                                            </div>
                                        </label>
                                        <label className="business-analyse-radio-option">
                                            <input
                                                type="radio"
                                                name="targetGender"
                                                value="female"
                                                checked={analysisData.targetAudience.gender === 'female'}
                                                onChange={(e) => updateNestedField('targetAudience', 'gender', e.target.value)}
                                            />
                                            <div className="business-analyse-radio-content">
                                                <i className="fas fa-venus"></i>
                                                <span>Weiblich</span>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <div className="business-analyse-form-group">
                                    <label>
                                        <i className="fas fa-euro-sign"></i>
                                        Einkommensklasse (Mehrfachauswahl möglich)
                                    </label>
                                    <div className="business-analyse-radio-group">
                                        <label className="business-analyse-radio-option">
                                            <input
                                                type="checkbox"
                                                checked={(analysisData.targetAudience.incomeClass || []).includes('low')}
                                                onChange={() => toggleArrayValue('targetAudience', 'incomeClass', 'low')}
                                            />
                                            <div className="business-analyse-radio-content">
                                                <i className="fas fa-euro-sign"></i>
                                                <span>Niedriges Einkommen</span>
                                                <small>&lt; €35k</small>
                                            </div>
                                        </label>
                                        <label className="business-analyse-radio-option">
                                            <input
                                                type="checkbox"
                                                checked={(analysisData.targetAudience.incomeClass || []).includes('middle')}
                                                onChange={() => toggleArrayValue('targetAudience', 'incomeClass', 'middle')}
                                            />
                                            <div className="business-analyse-radio-content">
                                                <i className="fas fa-euro-sign"></i>
                                                <span>Mittelschicht</span>
                                                <small>€35k - €100k</small>
                                            </div>
                                        </label>
                                        <label className="business-analyse-radio-option">
                                            <input
                                                type="checkbox"
                                                checked={(analysisData.targetAudience.incomeClass || []).includes('high')}
                                                onChange={() => toggleArrayValue('targetAudience', 'incomeClass', 'high')}
                                            />
                                            <div className="business-analyse-radio-content">
                                                <i className="fas fa-coins"></i>
                                                <span>Hohes Einkommen</span>
                                                <small>€100k+</small>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <div className="business-analyse-form-group">
                                    <label>
                                        <i className="fas fa-exclamation-circle"></i>
                                        Pain Points (Probleme & Herausforderungen)
                                    </label>
                                    <textarea
                                        value={analysisData.targetAudience.painPoints}
                                        onChange={(e) => updateNestedField('targetAudience', 'painPoints', e.target.value)}
                                        placeholder="Welche Probleme hat Ihre Zielgruppe? Was hält sie nachts wach?"
                                        className={`business-analyse-textarea ${analysisData.targetAudience.painPoints ? 'filled' : ''}`}
                                        rows="3"
                                    />
                                    <div className="business-analyse-input-helper">
                                        Je besser Sie die Probleme verstehen, desto gezielter können Sie helfen
                                    </div>
                                </div>

                                <div className="business-analyse-form-group">
                                    <label>
                                        <i className="fas fa-bullseye"></i>
                                        Ziele & Wünsche
                                    </label>
                                    <textarea
                                        value={analysisData.targetAudience.goals}
                                        onChange={(e) => updateNestedField('targetAudience', 'goals', e.target.value)}
                                        placeholder="Was möchte Ihre Zielgruppe erreichen? Welche Träume haben sie?"
                                        className={`business-analyse-textarea ${analysisData.targetAudience.goals ? 'filled' : ''}`}
                                        rows="3"
                                    />
                                    <div className="business-analyse-input-helper">
                                        Helfen Sie Ihrer Zielgruppe, ihre Ziele zu erreichen
                                    </div>
                                </div>

                                <div className="business-analyse-form-group">
                                    <label>
                                        <i className="fas fa-user-times"></i>
                                        Anti-Kunde (Wer ist NICHT Ihre Zielgruppe?)
                                    </label>
                                    <textarea
                                        value={analysisData.targetAudience.antiCustomer}
                                        onChange={(e) => updateNestedField('targetAudience', 'antiCustomer', e.target.value)}
                                        placeholder="Beschreiben Sie, wer definitiv NICHT zu Ihrer Zielgruppe gehört. Wen wollen Sie bewusst ausschließen?"
                                        className={`business-analyse-textarea ${analysisData.targetAudience.antiCustomer ? 'filled' : ''}`}
                                        rows="3"
                                    />
                                    <div className="business-analyse-input-helper">
                                        Klarheit über den Anti-Kunden hilft, Ihre echte Zielgruppe besser zu definieren
                                    </div>
                                </div>
                            </div>

                                <div className="business-analyse-form-group">
                                    <label>
                                        <i className="fas fa-globe"></i>
                                        Geografischer Fokus
                                    </label>
                                    <input
                                        type="text"
                                        value={analysisData.geoFocus}
                                        onChange={(e) => updateField('geoFocus', e.target.value)}
                                        placeholder="z.B. Deutschland, DACH-Region, Europa, Global"
                                        className={`business-analyse-input ${analysisData.geoFocus ? 'filled' : ''}`}
                                    />
                                </div>

                                <div className="business-analyse-form-group">
                                    <label>
                                    <i className="fas fa-chart-bar"></i>
                                    Marktsättigung
                                    </label>
                                    <div className="business-analyse-radio-group">
                                        <label className="business-analyse-radio-option">
                                            <input
                                                type="radio"
                                            name="marketSaturation"
                                            value="under100"
                                            checked={analysisData.marketSaturation === 'under100'}
                                            onChange={(e) => updateField('marketSaturation', e.target.value)}
                                            />
                                            <div className="business-analyse-radio-content">
                                            <i className="fas fa-leaf"></i>
                                            <span>Niedrig</span>
                                            <small>&lt; 100 Konkurrenten</small>
                                            </div>
                                        </label>
                                        <label className="business-analyse-radio-option">
                                            <input
                                                type="radio"
                                            name="marketSaturation"
                                            value="100-1000"
                                            checked={analysisData.marketSaturation === '100-1000'}
                                            onChange={(e) => updateField('marketSaturation', e.target.value)}
                                            />
                                            <div className="business-analyse-radio-content">
                                            <i className="fas fa-chart-line"></i>
                                            <span>Mittel</span>
                                            <small>100-1000</small>
                                            </div>
                                        </label>
                                        <label className="business-analyse-radio-option">
                                            <input
                                                type="radio"
                                            name="marketSaturation"
                                            value="over1000"
                                            checked={analysisData.marketSaturation === 'over1000'}
                                            onChange={(e) => updateField('marketSaturation', e.target.value)}
                                            />
                                            <div className="business-analyse-radio-content">
                                            <i className="fas fa-fire"></i>
                                            <span>Hoch</span>
                                            <small>&gt; 1000</small>
                                            </div>
                                        </label>
                                </div>
                            </div>

                            <div className="business-analyse-form-group">
                                <label>
                                    <i className="fas fa-calendar-alt"></i>
                                    Gründung / Aktivitätsstart
                                </label>
                                <input
                                    type="date"
                                    value={analysisData.foundingDate}
                                    onChange={(e) => updateField('foundingDate', e.target.value)}
                                    className={`business-analyse-input ${analysisData.foundingDate ? 'filled' : ''}`}
                                />
                                <div className="business-analyse-input-helper">
                                    Wann haben Sie mit Ihrem Business begonnen?
                                </div>
                            </div>
                        </div>
                    )}

                    {currentSection === 'branding' && (
                        <div className="business-analyse-section">
                            <div className="business-analyse-section-header">
                                <h2 className="business-analyse-section-title">
                                    <i className="fas fa-palette"></i>
                                    Markenimage & Branding
                                </h2>
                                <p className="business-analyse-section-subtitle">
                                    Bewerten Sie die visuelle und emotionale Wirkung Ihrer Marke
                                </p>
                            </div>
                            
                            <div className="business-analyse-form-group">
                                <label>
                                    <i className="fas fa-heart"></i>
                                    Was verkörpert Ihre Marke?
                                </label>
                                <textarea
                                    value={analysisData.branding.embodiment}
                                    onChange={(e) => updateNestedField('branding', 'embodiment', e.target.value)}
                                    placeholder="Beschreiben Sie, wofür Ihre Marke steht: Werte, Mission, Persönlichkeit..."
                                    className={`business-analyse-textarea ${analysisData.branding.embodiment ? 'filled' : ''}`}
                                    rows="4"
                                />
                                <div className="business-analyse-input-helper">
                                    z.B. Innovation, Vertrauen, Nachhaltigkeit, Luxus, Authentizität
                                </div>
                            </div>

                            <div className="business-analyse-form-group">
                                <label>
                                    <i className="fas fa-brain"></i>
                                    Emotionale Trigger
                                </label>
                                <textarea
                                    value={analysisData.branding.emotionalTriggers}
                                    onChange={(e) => updateNestedField('branding', 'emotionalTriggers', e.target.value)}
                                    placeholder="Welche Emotionen löst Ihre Marke bei Kunden aus?"
                                    className={`business-analyse-textarea ${analysisData.branding.emotionalTriggers ? 'filled' : ''}`}
                                    rows="4"
                                />
                                <div className="business-analyse-input-helper">
                                    z.B. Sicherheit, Aufregung, Stolz, Zugehörigkeit, Inspiration
                                </div>
                            </div>

                            <div className="business-analyse-form-group">
                                <label>
                                    <i className="fas fa-palette"></i>
                                    Design & Farbpsychologie
                                </label>
                                <textarea
                                    value={analysisData.branding.design}
                                    onChange={(e) => updateNestedField('branding', 'design', e.target.value)}
                                    placeholder="Beschreiben Sie Ihr Design-System, Farbwahl und deren psychologische Wirkung..."
                                    className={`business-analyse-textarea ${analysisData.branding.design ? 'filled' : ''}`}
                                    rows="3"
                                />
                                <div className="business-analyse-input-helper">
                                    z.B. Minimalismus, Blau für Vertrauen, Rot für Energie, moderne Sans-Serif Schrift
                                </div>
                            </div>

                            <div className="business-analyse-form-group">
                                <label>
                                    <i className="fas fa-comment-dots"></i>
                                    Wording & Tonalität
                                </label>
                                <textarea
                                    value={analysisData.branding.wording}
                                    onChange={(e) => updateNestedField('branding', 'wording', e.target.value)}
                                    placeholder="Wie kommunizieren Sie? Welche Sprache und Tonalität nutzen Sie?"
                                    className={`business-analyse-textarea ${analysisData.branding.wording ? 'filled' : ''}`}
                                    rows="3"
                                />
                                <div className="business-analyse-input-helper">
                                    z.B. Du vs. Sie, locker vs. professionell, humorvoll, direkt, inspirierend
                                </div>
                            </div>

                            <div className="business-analyse-form-group">
                                <label>
                                    <i className="fas fa-gem"></i>
                                    Kernwerte
                                </label>
                                <textarea
                                    value={analysisData.branding.values}
                                    onChange={(e) => updateNestedField('branding', 'values', e.target.value)}
                                    placeholder="Was sind Ihre fundamentalen Unternehmenswerte?"
                                    className={`business-analyse-textarea ${analysisData.branding.values ? 'filled' : ''}`}
                                    rows="3"
                                />
                                <div className="business-analyse-input-helper">
                                    z.B. Integrität, Innovation, Nachhaltigkeit, Kundenfokus, Transparenz
                                </div>
                            </div>

                            <div className="business-analyse-form-group">
                                <label>
                                    <i className="fas fa-user-secret"></i>
                                    Versteckte Psychologie
                                </label>
                                <textarea
                                    value={analysisData.branding.hiddenPsychology}
                                    onChange={(e) => updateNestedField('branding', 'hiddenPsychology', e.target.value)}
                                    placeholder="Welche psychologischen Prinzipien nutzen Sie bewusst oder unbewusst?"
                                    className={`business-analyse-textarea ${analysisData.branding.hiddenPsychology ? 'filled' : ''}`}
                                    rows="3"
                                />
                                <div className="business-analyse-input-helper">
                                    z.B. Verknappung, Social Proof, Autorität, Reziprozität, Storytelling-Muster
                                </div>
                            </div>

                            <div className="business-analyse-slider-group">
                                <label>
                                    <i className="fas fa-star"></i>
                                    Branding-Qualität
                                </label>
                                <div className="business-analyse-slider-container">
                                    <div className="business-analyse-slider-labels">
                                        <span>Schlecht</span>
                                        <span>Perfekt</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={analysisData.branding.brandingQuality}
                                        onChange={(e) => updateNestedField('branding', 'brandingQuality', parseInt(e.target.value))}
                                        className="business-analyse-slider"
                                        style={{
                                            background: `linear-gradient(to right, #ff6b9d 0%, #ffd93d 50%, #00ff88 100%)`
                                        }}
                                    />
                                    <div className="business-analyse-slider-value-container">
                                        <span className="business-analyse-slider-value">
                                            {analysisData.branding.brandingQuality}/10
                                        </span>
                                        <span className="business-analyse-slider-description">
                                            {analysisData.branding.brandingQuality <= 3 ? 'Verbesserungsbedarf' :
                                             analysisData.branding.brandingQuality <= 6 ? 'Durchschnittlich' :
                                             analysisData.branding.brandingQuality <= 8 ? 'Gut' : 'Exzellent'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="business-analyse-slider-group">
                                <label>
                                    <i className="fas fa-align-center"></i>
                                    Message-Design Übereinstimmung
                                </label>
                                <div className="business-analyse-slider-container">
                                    <div className="business-analyse-slider-labels">
                                        <span>Nicht stimmig</span>
                                        <span>Perfekt abgestimmt</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={analysisData.branding.messageDesignAlignment}
                                        onChange={(e) => updateNestedField('branding', 'messageDesignAlignment', parseInt(e.target.value))}
                                        className="business-analyse-slider"
                                    />
                                    <div className="business-analyse-slider-value-container">
                                        <span className="business-analyse-slider-value">
                                            {analysisData.branding.messageDesignAlignment}/10
                                        </span>
                                        <span className="business-analyse-slider-description">
                                            Wie gut spiegelt sich Ihre Message im Design wider?
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="business-analyse-slider-group">
                                <label>
                                    <i className="fas fa-camera"></i>
                                    Foto-Qualität
                                </label>
                                <div className="business-analyse-slider-container">
                                    <div className="business-analyse-slider-labels">
                                        <span>Smartphone-Fotos</span>
                                        <span>Professionell</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={analysisData.branding.photoQuality}
                                        onChange={(e) => updateNestedField('branding', 'photoQuality', parseInt(e.target.value))}
                                        className="business-analyse-slider"
                                    />
                                    <div className="business-analyse-slider-value-container">
                                        <span className="business-analyse-slider-value">
                                            {analysisData.branding.photoQuality}/10
                                        </span>
                                        <span className="business-analyse-slider-description">
                                            Bewerten Sie die Qualität Ihrer Fotos
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="business-analyse-slider-group">
                                <label>
                                    <i className="fas fa-video"></i>
                                    Video-Material Qualität
                                </label>
                                <div className="business-analyse-slider-container">
                                    <div className="business-analyse-slider-labels">
                                        <span>Kein/Einfaches Video</span>
                                        <span>Hochwertige Produktion</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={analysisData.branding.videoMaterial}
                                        onChange={(e) => updateNestedField('branding', 'videoMaterial', parseInt(e.target.value))}
                                        className="business-analyse-slider"
                                    />
                                    <div className="business-analyse-slider-value-container">
                                        <span className="business-analyse-slider-value">
                                            {analysisData.branding.videoMaterial}/10
                                        </span>
                                        <span className="business-analyse-slider-description">
                                            Haben Sie hochwertiges Videomaterial?
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Continue with other sections... */}
                    {currentSection === 'leadSources' && (
                        <div className="business-analyse-section">
                            <div className="business-analyse-section-header">
                                <h2 className="business-analyse-section-title">
                                    <i className="fas fa-funnel-dollar"></i>
                                    Lead-Quellen
                                </h2>
                                <p className="business-analyse-section-subtitle">
                                    Wählen Sie Ihre Kundenakquise-Kanäle - klicken Sie auf eine Quelle um sie hinzuzufügen
                                </p>
                            </div>
                            
                            <LeadSourceSelector
                                selectedSources={analysisData.leadSources}
                                onSourcesChange={(newSources) => updateField('leadSources', newSources)}
                            />
                        </div>
                    )}

                    {/* Products Section */}
                    {currentSection === 'products' && (
                        <div className="business-analyse-section">
                            <div className="business-analyse-section-header">
                                <h2 className="business-analyse-section-title">
                                    <i className="fas fa-box"></i>
                                    Produkte & Services
                                </h2>
                                <p className="business-analyse-section-subtitle">
                                    Ihre Angebote mit Preisen, Typ und Details
                                </p>
                            </div>
                            
                            <ProductManager
                                products={analysisData.products}
                                onProductsChange={(newProducts) => updateField('products', newProducts)}
                            />
                        </div>
                    )}

                    {/* Social Proof Section */}
                    {currentSection === 'socialProof' && (
                        <div className="business-analyse-section">
                            <div className="business-analyse-section-header">
                                <h2 className="business-analyse-section-title">
                                    <i className="fas fa-certificate"></i>
                                    Social Proof & Vertrauen
                                </h2>
                                <p className="business-analyse-section-subtitle">
                                    Definieren Sie Ihre Vertrauenssignale und Glaubwürdigkeitsindikatoren
                                </p>
                            </div>
                            
                            <SocialProofManager
                                socialProof={analysisData.socialProof}
                                onSocialProofChange={(newSocialProof) => setAnalysisData({ ...analysisData, socialProof: newSocialProof })}
                            />
                        </div>
                    )}

                    {/* Analysis Section */}
                    {currentSection === 'analysis' && (() => {
                        const analysis = calculateBusinessStrength();
                        return (
                            <div className="business-analyse-section">
                                <div className="business-analyse-section-header">
                                    <h2 className="business-analyse-section-title">
                                        <i className="fas fa-chart-line"></i>
                                        Business Stärke Analyse
                                    </h2>
                                    <p className="business-analyse-section-subtitle">
                                        Umfassende Bewertung Ihrer Business-Performance
                                    </p>
                                </div>

                                {/* Overall Score Card */}
                                <div className="business-analyse-analysis-overall">
                                    <div className="business-analyse-analysis-score-circle">
                                        <svg viewBox="0 0 120 120" className="business-analyse-score-svg">
                                            <circle cx="60" cy="60" r="54" fill="none" stroke="#e2e8f0" strokeWidth="8"/>
                                            <circle 
                                                cx="60" cy="60" r="54" 
                                                fill="none" 
                                                stroke={getScoreColor(analysis.scores.overall)}
                                                strokeWidth="8"
                                                strokeDasharray={`${(analysis.scores.overall / 100) * 339.292} 339.292`}
                                                strokeLinecap="round"
                                                transform="rotate(-90 60 60)"
                                            />
                                        </svg>
                                        <div className="business-analyse-score-content">
                                            <span className="business-analyse-score-number">{analysis.scores.overall}</span>
                                            <span className="business-analyse-score-label">/ 100</span>
                                        </div>
                                    </div>
                                    <div className="business-analyse-analysis-rating">
                                        <h3>Business Bewertung: <span className={`business-analyse-rating-${analysis.rating.toLowerCase().replace(' ', '-')}`}>{analysis.rating}</span></h3>
                                        <p>Basierend auf umfassender Analyse von Branding, Lead-Quellen, Produkten und Social Proof.</p>
                                    </div>
                                </div>

                                {/* Detailed Scores */}
                                <div className="business-analyse-analysis-details">
                                    <h3>Kategorie-Übersicht</h3>
                                    <div className="business-analyse-analysis-categories">
                                        <div className="business-analyse-analysis-category">
                                            <div className="business-analyse-category-header">
                                                <i className="fas fa-palette"></i>
                                                <span>Branding-Qualität</span>
                                                <strong>{Math.round(analysis.scores.branding)}/100</strong>
                                            </div>
                                            <div className="business-analyse-category-bar">
                                                <div 
                                                    className="business-analyse-category-fill"
                                                    style={{ 
                                                        width: `${analysis.scores.branding}%`,
                                                        background: getScoreColor(analysis.scores.branding)
                                                    }}
                                                ></div>
                                            </div>
                                        </div>

                                        <div className="business-analyse-analysis-category">
                                            <div className="business-analyse-category-header">
                                                <i className="fas fa-funnel-dollar"></i>
                                                <span>Lead-Quellen-Stärke</span>
                                                <strong>{Math.round(analysis.scores.leadDiversity)}/100</strong>
                                            </div>
                                            <div className="business-analyse-category-bar">
                                                <div 
                                                    className="business-analyse-category-fill"
                                                    style={{ 
                                                        width: `${analysis.scores.leadDiversity}%`,
                                                        background: getScoreColor(analysis.scores.leadDiversity)
                                                    }}
                                                ></div>
                                            </div>
                                        </div>

                                        <div className="business-analyse-analysis-category">
                                            <div className="business-analyse-category-header">
                                                <i className="fas fa-box"></i>
                                                <span>Produkt-Portfolio</span>
                                                <strong>{Math.round(analysis.scores.productPortfolio)}/100</strong>
                                            </div>
                                            <div className="business-analyse-category-bar">
                                                <div 
                                                    className="business-analyse-category-fill"
                                                    style={{ 
                                                        width: `${analysis.scores.productPortfolio}%`,
                                                        background: getScoreColor(analysis.scores.productPortfolio)
                                                    }}
                                                ></div>
                                            </div>
                                        </div>

                                        <div className="business-analyse-analysis-category">
                                            <div className="business-analyse-category-header">
                                                <i className="fas fa-certificate"></i>
                                                <span>Social Proof Stärke</span>
                                                <strong>{Math.round(analysis.scores.socialProof)}/100</strong>
                                            </div>
                                            <div className="business-analyse-category-bar">
                                                <div 
                                                    className="business-analyse-category-fill"
                                                    style={{ 
                                                        width: `${analysis.scores.socialProof}%`,
                                                        background: getScoreColor(analysis.scores.socialProof)
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Strengths */}
                                {analysis.strengths.length > 0 && (
                                    <div className="business-analyse-analysis-strengths">
                                        <h3><i className="fas fa-star"></i> Ihre Stärken</h3>
                                        <div className="business-analyse-strength-list">
                                            {analysis.strengths.map((strength, index) => (
                                                <div key={index} className="business-analyse-strength-item">
                                                    <i className="fas fa-check-circle"></i>
                                                    <span>{strength}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Recommendations */}
                                {analysis.recommendations.length > 0 && (
                                    <div className="business-analyse-analysis-recommendations">
                                        <h3><i className="fas fa-lightbulb"></i> Empfehlungen & Optimierungspotenzial</h3>
                                        <div className="business-analyse-recommendation-list">
                                            {analysis.recommendations.map((rec, index) => (
                                                <div key={index} className={`business-analyse-recommendation-item priority-${rec.priority}`}>
                                                    <div className="business-analyse-recommendation-header">
                                                        <span className={`business-analyse-priority-badge ${rec.priority}`}>
                                                            {rec.priority === 'high' ? '🔴 Hohe Priorität' : 
                                                             rec.priority === 'medium' ? '🟡 Mittlere Priorität' : 
                                                             '🟢 Niedrige Priorität'}
                                                        </span>
                                                        <span className="business-analyse-recommendation-category">{rec.category}</span>
                                                    </div>
                                                    <p>{rec.message}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Action CTA */}
                                <div className="business-analyse-analysis-cta">
                                    <div className="business-analyse-cta-content">
                                        <h3>Bereit Ihr Business zu stärken?</h3>
                                        <p>Nutzen Sie diese Insights für strategische Entscheidungen und beschleunigtes Wachstum.</p>
                                        <button 
                                            className="business-analyse-btn-primary"
                                            onClick={() => navigate('/business-analysis')}
                                        >
                                            <i className="fas fa-check"></i>
                                            Analyse abschließen
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Navigation Buttons */}
                    <div className="business-analyse-section-nav">
                        <button
                            className="business-analyse-btn-secondary"
                            onClick={() => {
                                const currentIndex = sections.findIndex(s => s.id === currentSection);
                                if (currentIndex > 0) {
                                    handleSectionChange(sections[currentIndex - 1].id);
                                }
                            }}
                            disabled={sections.findIndex(s => s.id === currentSection) === 0}
                        >
                            <i className="fas fa-arrow-left"></i>
                            Previous
                        </button>

                        <button
                            className="business-analyse-btn-primary"
                            onClick={() => {
                                const currentIndex = sections.findIndex(s => s.id === currentSection);
                                if (currentIndex < sections.length - 1) {
                                    handleSectionChange(sections[currentIndex + 1].id);
                                }
                            }}
                            disabled={sections.findIndex(s => s.id === currentSection) === sections.length - 1}
                        >
                            Next
                            <i className="fas fa-arrow-right"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BusinessAnalysisAdd;
